import { NavLink, useLocation } from 'react-router';

export interface ViewDefinition {
  id: string;
  label: string;
  search: string;
  /** Optional row count rendered as a pill to the right of the label. */
  count?: number;
}

interface Props {
  title: string;
  views: ViewDefinition[];
  basePath: string;
}

// Strip non-view params (e.g. ?q=...) before comparing so a search-within-view
// doesn't deselect the active sidebar entry. Kept as exact-string equality on
// the remaining params to avoid one view falsely matching another's superset.
const NON_VIEW_PARAMS = new Set(['q']);
function canonicalize(search: string): string {
  const params = new URLSearchParams(search);
  for (const k of NON_VIEW_PARAMS) params.delete(k);
  // Sort for stable equality regardless of param order.
  const entries = [...params].sort(([a], [b]) => a.localeCompare(b));
  return new URLSearchParams(entries).toString();
}

export function ViewsSidebar({ title, views, basePath }: Props): React.ReactElement {
  const location = useLocation();
  const rawSearch = location.pathname === basePath ? location.search.replace(/^\?/, '') : '';
  const currentCanonical = canonicalize(rawSearch);

  return (
    <aside className="w-full lg:w-56 lg:shrink-0">
      <div className="sticky top-6">
        <div className="mb-2 px-2 text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">
          {title}
        </div>
        <nav className="flex flex-col gap-0.5">
          {views.map((v) => {
            const isActive = currentCanonical === canonicalize(v.search);
            return (
              <NavLink
                key={v.id}
                to={v.search ? `${basePath}?${v.search}` : basePath}
                end
                className={
                  'flex items-center justify-between gap-2 rounded-[var(--radius-button)] px-3 py-2 text-sm transition ' +
                  (isActive
                    ? 'bg-[var(--color-t3-blue)]/10 font-semibold text-[var(--color-t3-blue)]'
                    : 'text-[var(--color-body)] hover:bg-[var(--color-surface)]')
                }
              >
                <span className="truncate">{v.label}</span>
                {v.count !== undefined && (
                  <span
                    className={
                      'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums ' +
                      (isActive
                        ? 'bg-[var(--color-t3-blue)]/15 text-[var(--color-t3-blue)]'
                        : 'bg-[var(--color-surface)] text-[var(--color-muted)]')
                    }
                  >
                    {v.count.toLocaleString()}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
      </div>
    </aside>
  );
}
